import React, { memo, useState, useCallback } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import { IconCheck, IconStar, IconStarFilled } from "@tabler/icons-react";
import "./Modals.css";

/**
 * Modal de finalização de atendimento
 */
const FinishServiceModal = memo(({
  show,
  onHide,
  onFinish,
  conversation,
  isFinishing = false,
}) => {
  const [note, setNote] = useState("");
  const [rating, setRating] = useState(0);
  const [resolution, setResolution] = useState("resolved"); // resolved, pending, unresolved

  const handleFinish = useCallback(async () => {
    await onFinish({
      note: note.trim(),
      rating,
      resolution,
    });

    // Reset form
    setNote("");
    setRating(0);
    setResolution("resolved");
    onHide();
  }, [note, rating, resolution, onFinish, onHide]);

  const handleClose = useCallback(() => {
    setNote("");
    setRating(0);
    setResolution("resolved");
    onHide();
  }, [onHide]);

  // Rating stars component
  const RatingStars = () => (
    <div className="sc-modal__rating">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          className={`sc-modal__star ${rating >= star ? "active" : ""}`}
          onClick={() => setRating(star === rating ? 0 : star)}
        >
          {rating >= star ? (
            <IconStarFilled size={24} />
          ) : (
            <IconStar size={24} />
          )}
        </button>
      ))}
      {rating > 0 && (
        <span className="sc-modal__rating-label">
          {rating === 1 && "Muito insatisfeito"}
          {rating === 2 && "Insatisfeito"}
          {rating === 3 && "Neutro"}
          {rating === 4 && "Satisfeito"}
          {rating === 5 && "Muito satisfeito"}
        </span>
      )}
    </div>
  );

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      className="sc-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title className="sc-modal__title">
          <IconCheck size={22} />
          Finalizar Atendimento
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Info do contato */}
        {conversation && (
          <div className="sc-modal__info-box">
            <span className="sc-modal__info-label">Contato:</span>
            <span className="sc-modal__info-value">
              {conversation.name || conversation.phone}
            </span>
          </div>
        )}

        {/* Resolução */}
        <Form.Group className="mb-3">
          <Form.Label>Status da resolução:</Form.Label>
          <div className="sc-modal__radio-group">
            <Form.Check
              type="radio"
              id="resolution-resolved"
              label="Resolvido"
              checked={resolution === "resolved"}
              onChange={() => setResolution("resolved")}
            />
            <Form.Check
              type="radio"
              id="resolution-pending"
              label="Pendente (aguardando retorno)"
              checked={resolution === "pending"}
              onChange={() => setResolution("pending")}
            />
            <Form.Check
              type="radio"
              id="resolution-unresolved"
              label="Não resolvido"
              checked={resolution === "unresolved"}
              onChange={() => setResolution("unresolved")}
            />
          </div>
        </Form.Group>

        {/* Avaliação do atendimento */}
        <Form.Group className="mb-3">
          <Form.Label>Avaliação do atendimento (opcional):</Form.Label>
          <RatingStars />
        </Form.Group>

        {/* Nota de finalização */}
        <Form.Group className="mb-3">
          <Form.Label>Observações finais (opcional):</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Descreva como o atendimento foi finalizado..."
          />
        </Form.Group>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isFinishing}>
          Cancelar
        </Button>
        <Button
          variant="success"
          onClick={handleFinish}
          disabled={isFinishing}
        >
          {isFinishing ? (
            "Finalizando..."
          ) : (
            <>
              <IconCheck size={16} />
              Finalizar
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
});

FinishServiceModal.displayName = "FinishServiceModal";

export default FinishServiceModal;
